import {
  Box,
  Checkbox,
  Collapse,
  Grid,
  IconButton,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { GetInspectionItem } from '../../../../../../services/apiTypes';
import CheckBox from '../../Input/CheckBox';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateJudgement } from '../../../../../../stores/inspectionTypeData/slice';
import {
  getColorByJudgement,
  isEmpty,
  isNa,
  isNg,
  isOk,
} from '../../../../../../_utils/inspectionUtils';
import {
  JUDGEMENT,
  NG_DELAY,
} from '../../../../../../_constants/inspectionConst';
import InspectionDetailModal, { DetailItem } from './InspectionDetailModal';
import React from 'react';
import { useTranslation } from 'react-i18next';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { TEXT_OVERFLOW_2LINES } from '../../../../../../_constants/styleConst';
import { RootState } from '../../../../../../stores/store';

type AccordionProps = {
  typeLabel: string;
  itemDataList: GetInspectionItem[];
  beforeItemDataList: GetInspectionItem[];
  isUnfilled: boolean;
  isVisualInspection: boolean;
};

/** アコーディオン付きで表示するパターン */
const Accordion: React.FC<AccordionProps> = ({
  typeLabel,
  itemDataList,
  beforeItemDataList,
  isUnfilled,
  isVisualInspection,
}) => {
  const theme = useTheme();
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { categoryId, typeId } = useSelector(
    (state: RootState) => state.selectIds,
  );
  /** 問題箇所(階層2)を含む項目があるか */
  const [hasDetailInType, setHasDetailInType] = useState(false);
  /** 判定基準のテキストがある項目を含むか */
  const [hasCriteriaInType, setHasCriteriaInType] = useState(false);
  /** アコーディオンの開閉状態 */
  const [isExpandList, setIsExpandList] = useState<boolean[]>([]);
  /** 問題箇所選択の処理を実行中か(ディレイがあるので開閉フラグとは別で持つ) */
  const [isExecutingNg, setIsExecutingNg] = useState(false);
  /** 問題箇所選択選択ダイアログの開閉フラグ */
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false);
  /** 問題箇所選択ダイアログのタイトル */
  const [title, setTitle] = useState('');
  /** 問題箇所のリスト */
  const [details, setDetails] = useState<DetailItem[]>([]);

  /**
   * 開いているダイアログのIDパス
   * path[0] : 検査部位・内容のID
   * path[1] : 現象のID
   */
  const [path, setPath] = useState<string[]>([]);

  useEffect(() => {
    // 表示切替用フラグの初期化
    setHasDetailInType(
      itemDataList.some((depth1) =>
        depth1.itemData.some((depth2) => depth2.itemData.length > 0),
      ),
    );
    setHasCriteriaInType(
      itemDataList.some(({ criteria }) => criteria.text !== ''),
    );
  }, [typeId]);

  useEffect(() => {
    /** アコーディオンの開閉状態を初期化 */
    setIsExpandList(
      itemDataList.map(
        (item) => isEmpty(item.judgement) || isNg(item.judgement),
      ),
    );
  }, [itemDataList]);

  const style = {
    label: {
      fontSize: theme.typography.caption.fontSize ?? '0.75rem',
      textAlign: 'center',
      alignSelf: 'end',
    },
    input: {
      height: '100%',
      padding: '0px',
    },
  };

  const handleOpen = (item: GetInspectionItem, problem: GetInspectionItem) => {
    if (problem.itemData.length === 0) {
      return;
    }
    const newDetails: DetailItem[] = [];
    // 前回結果(階層0)
    const beforeItem = beforeItemDataList.find(
      (before0) => before0.id === item.id,
    );

    // 前回結果(階層1)
    const beforeProblem =
      beforeItem &&
      beforeItem.itemData.find((before1) => before1.id === problem.id);

    problem.itemData.forEach((detail) => {
      // 前回結果(階層2)
      const beforeDetail =
        beforeProblem &&
        beforeProblem.itemData.find((before2) => before2.id === detail.id);

      newDetails.push({
        id: detail.id,
        label: detail.label,
        isChecked: false,
        borderColor: beforeDetail && isNg(beforeDetail.judgement) ? 'red' : '',
      });
    });

    setIsOpenDetailModal(true);
    setTitle(item.label + '\t:\t' + problem.label);
    setDetails(newDetails);
    setPath([item.id, problem.id]);
  };

  const handleClose = (shouldUpdate: boolean) => {
    if (shouldUpdate) {
      const isAnyChecked = details.some((detail) => detail.isChecked);
      details.forEach((detail) => {
        // 1つもチェックが付いてない場合は空とする
        // それ以外はチェックあり→NG、チェックなし→OKとする
        const newJudgement = isAnyChecked ? (detail.isChecked ? 2 : 1) : 0;
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [...path, detail.id],
            judgement: newJudgement,
          }),
        );
      });
    } else {
      // キャンセルの場合は親のチェックをはずす
      dispatch(
        updateJudgement({
          categoryId,
          typeId,
          path,
          judgement: JUDGEMENT.OK,
        }),
      );
      details.forEach((detail) => {
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [...path, detail.id],
            judgement: JUDGEMENT.EMPTY,
          }),
        );
      });
    }
    setIsExecutingNg(false);
    setIsOpenDetailModal(false);
    setPath([]);
  };

  /** 指定したIDのアコーディオンの開閉状態を変更 */
  const toggleAccordionExpand = (index: number, isOpen: boolean) => {
    setIsExpandList((prev: boolean[]) => {
      const newState = [...prev];
      newState[index] = isOpen;
      return newState;
    });
  };

  /** 親の検査項目のチェックボックスをNGにした時 */
  const onNgParentItem = (
    itemId: string,
    problemList: GetInspectionItem[],
    index: number,
  ) => {
    toggleAccordionExpand(index, true);
    // 一旦OKにしておく
    problemList.forEach((problem) => {
      if (isEmpty(problem.judgement)) {
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [itemId, problem.id],
            judgement: JUDGEMENT.OK,
          }),
        );
        // 問題箇所も更新
        problem.itemData.forEach((detail) => {
          dispatch(
            updateJudgement({
              categoryId,
              typeId,
              path: [itemId, problem.id, detail.id],
              judgement: JUDGEMENT.OK,
            }),
          );
        });
      }
    });
  };

  /** 親の検査項目のチェックボックスをOK・N/Aにした時 */
  const clearProblemCheck = (
    item: GetInspectionItem,
    index: number,
    judgement: number,
  ) => {
    // アコーディオンを閉じる
    toggleAccordionExpand(index, false);
    item.itemData.forEach((problem) => {
      dispatch(
        updateJudgement({
          categoryId,
          typeId,
          path: [item.id, problem.id],
          judgement,
        }),
      );
      // 問題箇所があればクリア
      problem.itemData.forEach((detail) => {
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [item.id, problem.id, detail.id],
            judgement,
          }),
        );
      });
    });
  };

  /** アコーディオン内のチェックボックス押下時 */
  const onChangeProblemCheck = (
    isChecked: boolean,
    item: GetInspectionItem,
    problem: GetInspectionItem,
  ) => {
    if (isExecutingNg) {
      return;
    }

    // チェックを付ける
    if (isChecked) {
      // 紐づく親項目が空の場合はNGにする
      if (isEmpty(item.judgement)) {
        dispatch(
          updateJudgement({
            categoryId,
            typeId,
            path: [item.id],
            judgement: JUDGEMENT.NG,
          }),
        );
      }
      dispatch(
        updateJudgement({
          categoryId,
          typeId,
          path: [item.id, problem.id],
          judgement: JUDGEMENT.NG,
        }),
      );
      // 他の空のチェックボックスはOKにしておく
      item.itemData.forEach((p) => {
        if (p.id !== problem.id && isEmpty(p.judgement)) {
          dispatch(
            updateJudgement({
              categoryId,
              typeId,
              path: [item.id, p.id],
              judgement: JUDGEMENT.OK,
            }),
          );
        }
      });
      // 問題箇所あり
      if (problem.itemData.length > 0) {
        // ダイアログを閉じるまで操作不可にする
        setIsExecutingNg(true);
        const timer = setTimeout(() => {
          handleOpen(item, problem);
        }, NG_DELAY);
        return () => clearTimeout(timer);
      }
      return;
    }

    // チェックを外す
    dispatch(
      updateJudgement({
        categoryId,
        typeId,
        path: [item.id, problem.id],
        judgement: JUDGEMENT.OK,
      }),
    );
    // 問題箇所があればクリア
    problem.itemData.forEach((detail) => {
      dispatch(
        updateJudgement({
          categoryId,
          typeId,
          path: [item.id, problem.id, detail.id],
          judgement: JUDGEMENT.EMPTY,
        }),
      );
    });
  };

  /** 問題箇所に表示するテキスト取得 */
  const getDetailText = (problem: GetInspectionItem) => {
    const labels: string[] = [];
    problem.itemData.forEach((detail) => {
      if (isNg(detail.judgement)) {
        labels.push(detail.label);
      }
    });

    return labels.join(', ');
  };

  /** アコーディオンチェック漏れ確認 */
  const isAccordionError = (item: GetInspectionItem) => {
    return (
      isNg(item.judgement) &&
      item.itemData.length > 0 &&
      !item.itemData.some((item) => isNg(item.judgement))
    );
  };

  return (
    <Box>
      {/* ヘッダー */}
      <Grid container>
        <Grid size={8} mb={1} sx={{ display: 'flex' }}>
          {isVisualInspection && (
            <Box sx={{ display: 'inline', mx: 1.5 }}>
              <VisibilityIcon color="primary" />
            </Box>
          )}
          <Typography
            sx={{
              ...TEXT_OVERFLOW_2LINES,
              fontSize: theme.typography.body1.fontSize ?? '0.75rem',
            }}
          >
            {typeLabel}
          </Typography>
        </Grid>
        {hasDetailInType && hasCriteriaInType ? (
          <React.Fragment>
            <Grid size={2} sx={style.label}>
              {t('LBL_DEFECTIVE_POSITION')}
            </Grid>
            <Grid size={2} sx={style.label}>
              {t('LBL_CRITERIA')}
            </Grid>
          </React.Fragment>
        ) : (
          <React.Fragment>
            <Grid size={1} />
            <Grid size={3} sx={style.label}>
              {hasDetailInType
                ? t('LBL_DEFECTIVE_POSITION')
                : hasCriteriaInType
                  ? t('LBL_CRITERIA')
                  : ''}
            </Grid>
          </React.Fragment>
        )}
      </Grid>
      {itemDataList.map((item, i) => {
        const lineStyle = {
          height: '40px',
          alignItems: 'center',
          bgcolor: i % 2 === 0 ? theme.palette.background.paper : '',
        };
        return (
          <Grid key={`accordion_${item.id}_${i}`}>
            {/* 階層0 */}
            <Grid container sx={lineStyle}>
              <Grid size={0.5}></Grid>
              <Grid size={2.5}>
                <Typography
                  sx={[
                    { fontSize: theme.typography.caption.fontSize },
                    TEXT_OVERFLOW_2LINES,
                  ]}
                >
                  {item.label}
                </Typography>
              </Grid>
              {/* 階層0のチェックボックス */}
              <Grid size={1}>
                <CheckBox
                  itemId={`accordion_${i}`}
                  categoryId={categoryId}
                  typeId={typeId}
                  path={[item.id]}
                  judgement={item.judgement}
                  onOk={() => clearProblemCheck(item, i, JUDGEMENT.OK)}
                  onNg={() => onNgParentItem(item.id, item.itemData, i)}
                  onNa={() => clearProblemCheck(item, i, JUDGEMENT.NA)}
                  borderColor={getColorByJudgement(
                    beforeItemDataList.find((before) => before.id === item.id)
                      ?.judgement,
                  )}
                  isUnfilled={isUnfilled}
                />
              </Grid>
              {/* 開閉アイコン */}
              <Grid size={1}>
                {item.itemData.length > 0 && (
                  <IconButton
                    onClick={() => toggleAccordionExpand(i, !isExpandList[i])}
                  >
                    {isExpandList[i] ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                  </IconButton>
                )}
              </Grid>
              {/* 「1つ以上選択してください」のメッセージ */}
              <Grid
                size={4}
                sx={{ fontSize: theme.typography.caption.fontSize }}
                color="red"
              >
                {isAccordionError(item) && t('ERR_CHECK_THE_BOX')}
              </Grid>
              {hasCriteriaInType ? (
                <React.Fragment>
                  {hasDetailInType && <Grid size={1} />}
                  <Grid size={hasDetailInType ? 2 : 3} textAlign="center">
                    <Typography
                      sx={{ fontSize: theme.typography.caption.fontSize }}
                    >
                      {item.criteria.text}
                    </Typography>
                  </Grid>
                </React.Fragment>
              ) : (
                <Grid size={3} />
              )}
            </Grid>
            {/* アコーディオンの中 */}
            <Collapse in={isExpandList[i]} unmountOnExit>
              {item.itemData.map((problem, j) => {
                let borderColor = getColorByJudgement(
                  beforeItemDataList
                    .find((before) => before.id === item.id)
                    ?.itemData?.find((itemData) => itemData.id === problem.id)
                    ?.judgement,
                );
                borderColor = borderColor === 'red' ? 'red' : '';
                return (
                  <Grid
                    container
                    key={`accordion_${item.id}_${i}_${j}`}
                    sx={{
                      height: '36px',
                      alignItems: 'center',
                      bgcolor: lineStyle.bgcolor,
                    }}
                  >
                    <Grid size={4} />
                    <Grid size={1}>
                      <Checkbox
                        id={`accordion_check_${i}-${j}`}
                        size="small"
                        sx={{
                          color:
                            isAccordionError(item) && isUnfilled
                              ? theme.palette.warning.main
                              : borderColor,
                          '&.Mui-disabled': {
                            color: borderColor ? borderColor : '',
                          },
                        }}
                        onChange={(_, checked) =>
                          onChangeProblemCheck(checked, item, problem)
                        }
                        checked={isNg(problem.judgement)}
                        disabled={isOk(item.judgement) || isNa(item.judgement)}
                      />
                    </Grid>
                    <Grid
                      size={hasDetailInType && !hasCriteriaInType ? 4 : 3}
                      container
                      alignItems="center"
                    >
                      <Typography
                        sx={[
                          {
                            alignSelf: 'center',
                            fontSize: theme.typography.caption.fontSize,
                          },
                          TEXT_OVERFLOW_2LINES,
                        ]}
                      >
                        {problem.label}
                      </Typography>
                    </Grid>
                    {/* 問題箇所のボックス */}
                    {hasDetailInType ? (
                      <React.Fragment>
                        <Grid
                          size={hasCriteriaInType ? 2 : 3}
                          textAlign="center"
                        >
                          {problem.itemData.length > 0 && (
                            <TextField
                              value={getDetailText(problem)}
                              sx={{
                                height: '36px',
                                padding: '1px',
                              }}
                              slotProps={{
                                input: {
                                  readOnly: true,
                                  style: {
                                    height: '100%',
                                    padding: '0px',
                                    background:
                                      isNg(problem.judgement) &&
                                      getDetailText(problem) === '' &&
                                      isUnfilled
                                        ? theme.palette.warning.light
                                        : theme.palette.background.default,
                                  },
                                },
                              }}
                            />
                          )}
                        </Grid>
                        {hasCriteriaInType && <Grid size={2} />}
                      </React.Fragment>
                    ) : (
                      <Grid size={4} />
                    )}
                  </Grid>
                );
              })}
            </Collapse>
          </Grid>
        );
      })}
      <InspectionDetailModal
        isOpen={isOpenDetailModal}
        title={title}
        details={details}
        setDetails={setDetails}
        handleClose={handleClose}
      />
    </Box>
  );
};

export default Accordion;
